/**
 * All-deals table under the landscape — sorted by health score by default,
 * click a column header to re-sort, click a row to select it in the detail
 * panel. Renders the same band + scores the chart encodes so the table is
 * usable standalone (and screen-reader friendly where the bubbles aren't).
 */
import { useMemo, useState } from "react";

import { StatusBadge } from "@/components/cinch/StatusBadge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

import type { DealHealthDeal } from "../api";
import { fmtCents } from "../format";
import { pipelineShort } from "./dealHealthColors";

type SortKey =
  | "name"
  | "owner"
  | "pipeline"
  | "stage"
  | "amount"
  | "engagement"
  | "quality"
  | "urgency"
  | "touch"
  | "health";

const SORTERS: Record<SortKey, (d: DealHealthDeal) => string | number> = {
  name: (d) => d.name.toLowerCase(),
  owner: (d) => (d.owner_display_name ?? "").toLowerCase(),
  pipeline: (d) => d.pipeline_name ?? "",
  stage: (d) => d.stage_name ?? "",
  amount: (d) => d.amount_cents,
  engagement: (d) => d.scores.engagement,
  quality: (d) => d.scores.deal_quality,
  urgency: (d) => d.scores.urgency,
  // Never-touched sorts as most-stale.
  touch: (d) => d.days_since_touch ?? Number.MAX_SAFE_INTEGER,
  health: (d) => d.health_score,
};

// Sensible first-click direction per column: scores/amounts show biggest
// first, "last touch" shows most stale first, text sorts A→Z.
const DEFAULT_DESC: Record<SortKey, boolean> = {
  name: false,
  owner: false,
  pipeline: false,
  stage: false,
  amount: true,
  engagement: true,
  quality: true,
  urgency: true,
  touch: true,
  health: true,
};

function SortableHead({
  label,
  sortKey,
  current,
  desc,
  onSort,
  className,
}: {
  label: string;
  sortKey: SortKey;
  current: SortKey;
  desc: boolean;
  onSort: (k: SortKey) => void;
  className?: string;
}) {
  const active = current === sortKey;
  return (
    <TableHead
      className={cn("cursor-pointer select-none whitespace-nowrap", className)}
      onClick={() => onSort(sortKey)}
    >
      {label}
      {active && <span className="ml-1">{desc ? "↓" : "↑"}</span>}
    </TableHead>
  );
}

export function DealHealthTable({
  deals,
  selectedId,
  onSelect,
}: {
  deals: DealHealthDeal[];
  selectedId: string | null;
  onSelect: (deal: DealHealthDeal) => void;
}) {
  const [sortKey, setSortKey] = useState<SortKey>("health");
  const [desc, setDesc] = useState(true);

  const onSort = (k: SortKey) => {
    if (k === sortKey) {
      setDesc(!desc);
    } else {
      setSortKey(k);
      setDesc(DEFAULT_DESC[k]);
    }
  };

  const sorted = useMemo(() => {
    const getter = SORTERS[sortKey];
    return [...deals].sort((a, b) => {
      const va = getter(a);
      const vb = getter(b);
      const cmp = va < vb ? -1 : va > vb ? 1 : 0;
      return desc ? -cmp : cmp;
    });
  }, [deals, sortKey, desc]);

  return (
    <div className="overflow-x-auto rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <SortableHead label="Deal" sortKey="name" current={sortKey} desc={desc} onSort={onSort} />
            <SortableHead label="Owner" sortKey="owner" current={sortKey} desc={desc} onSort={onSort} />
            <SortableHead label="Pipeline" sortKey="pipeline" current={sortKey} desc={desc} onSort={onSort} />
            <SortableHead label="Stage" sortKey="stage" current={sortKey} desc={desc} onSort={onSort} />
            <SortableHead label="Amount" sortKey="amount" current={sortKey} desc={desc} onSort={onSort} className="text-right" />
            <SortableHead label="Health" sortKey="health" current={sortKey} desc={desc} onSort={onSort} className="text-right" />
            <SortableHead label="Engage" sortKey="engagement" current={sortKey} desc={desc} onSort={onSort} className="text-right" />
            <SortableHead label="Quality" sortKey="quality" current={sortKey} desc={desc} onSort={onSort} className="text-right" />
            <SortableHead label="Urgency" sortKey="urgency" current={sortKey} desc={desc} onSort={onSort} className="text-right" />
            <SortableHead label="Last touch" sortKey="touch" current={sortKey} desc={desc} onSort={onSort} className="text-right" />
            <TableHead>Band</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sorted.map((d) => (
            <TableRow
              key={d.hubspot_deal_id}
              className={cn(
                "cursor-pointer",
                d.hubspot_deal_id === selectedId && "bg-muted/60",
              )}
              onClick={() => onSelect(d)}
            >
              <TableCell className="max-w-[280px] truncate font-medium" title={d.name}>
                {d.name}
              </TableCell>
              <TableCell className="whitespace-nowrap">
                {d.owner_display_name ?? "—"}
              </TableCell>
              <TableCell className="whitespace-nowrap">
                {pipelineShort(d.pipeline_name)}
              </TableCell>
              <TableCell className="whitespace-nowrap">{d.stage_name ?? "—"}</TableCell>
              <TableCell className="text-right tabular-nums">
                {d.amount_cents > 0 ? fmtCents(d.amount_cents) : "—"}
              </TableCell>
              <TableCell className="text-right font-semibold tabular-nums">
                {Math.round(d.health_score)}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {Math.round(d.scores.engagement)}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {Math.round(d.scores.deal_quality)}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {Math.round(d.scores.urgency)}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {d.days_since_touch == null
                  ? "never"
                  : d.days_since_touch === 0
                  ? "today"
                  : `${d.days_since_touch}d`}
              </TableCell>
              <TableCell>
                <StatusBadge value={d.band} />
              </TableCell>
            </TableRow>
          ))}
          {sorted.length === 0 && (
            <TableRow>
              <TableCell colSpan={11} className="py-8 text-center text-muted-foreground">
                No deals match the current filter.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
